import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getToken } from '../services/keycloak';
import Modal from './Modal';

/**
 * Encode a multi-segment filename for use in a URL path without turning the
 * segment separators into %2F. Flask's `<path:filename>` converter does not
 * decode %2F back into a slash, so os.path.join receives a literal %2F in
 * the filename and the file lookup fails.
 *
 * @param {string} pdfFilename - Relative path like "queue/20260419_foo.pdf"
 * @returns {string} Path with each segment percent-encoded and joined by "/"
 */
function encodePdfPath(pdfFilename) {
  return pdfFilename.split('/').map(encodeURIComponent).join('/');
}

/**
 * Fetch a PDF from the backend and open it in a new browser tab.
 * Uses fetch (not direct navigation) so that CRA's dev proxy forwards the request.
 *
 * Phase 11: URL is `/api/pdf/<org_id>/<filename>`. The org_id is required
 * — backend authz checks the caller is a member of that org (or super admin)
 * and constructs the S3 key as `<org_id>/<filename>`.
 *
 * @param {number|string} orgId - Organization id that owns the PDF (from invoice.organization_id)
 * @param {string} pdfFilename - Basename as stored in invoices.pdf_filename
 */
async function openPdf(orgId, pdfFilename) {
  try {
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`/api/pdf/${orgId}/${encodePdfPath(pdfFilename)}`, { headers });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } catch (err) {
    console.error('Failed to open PDF:', err);
  }
}

/**
 * Strip a leading `/data/pdfs/` (or any leading `/`) from a stored filename
 * so it is a path relative to ALLOWED_PDF_BASE, which is what `/api/pdf/<path>`
 * expects. Legacy rows (pre-PR #82) stored absolute paths; newer rows are
 * already relative.
 *
 * @param {string} name - Filename as stored in the DB
 * @returns {string} Relative filename for `/api/pdf/<name>`
 */
function normalizePdfFilename(name) {
  if (!name || typeof name !== 'string') return name;
  let cleaned = name.trim();
  if (cleaned.startsWith('/data/pdfs/')) cleaned = cleaned.slice('/data/pdfs/'.length);
  while (cleaned.startsWith('/')) cleaned = cleaned.slice(1);
  return cleaned;
}

/**
 * Parse PDF filenames from an invoice, supporting both the new pdf_filenames
 * array from the backend and the legacy pdf_filename string/JSON field.
 *
 * @param {Object} invoice - Invoice object from the API
 * @returns {string[]} Array of PDF filename strings, normalized to be relative
 *   to ALLOWED_PDF_BASE
 */
function getPdfFilenames(invoice) {
  if (!invoice) return [];
  let names = [];
  if (Array.isArray(invoice.pdf_filenames) && invoice.pdf_filenames.length > 0) {
    names = invoice.pdf_filenames;
  } else {
    const raw = invoice.pdf_filename;
    if (!raw) return [];
    if (raw.startsWith('[')) {
      try {
        names = JSON.parse(raw);
      } catch {
        names = [raw];
      }
    } else {
      names = [raw];
    }
  }
  return names.map(normalizePdfFilename).filter(Boolean);
}

/**
 * Modal that displays invoice details with editable category dropdowns per line item.
 * Supports prev/next navigation through an invoice list via arrow keys or buttons.
 * Uses the enhanced Modal component for consistent dialog behavior.
 *
 * @param {Object} props
 * @param {number} props.invoiceId - The invoice ID to display
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback when closing the modal
 * @param {Array} [props.invoices] - Full list of invoices for prev/next navigation
 * @param {number} [props.currentIndex] - Index of the current invoice in the list
 * @param {Function} [props.onNavigate] - Callback with new index for prev/next navigation
 * @returns {JSX.Element|null} The modal or null when closed
 */
function InvoiceDetailModal({ invoiceId, isOpen, onClose, invoices, currentIndex, onNavigate }) {
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updateMessage, setUpdateMessage] = useState(null);
  const dialogRef = useRef(null);

  /**
   * Load invoice data and categories when modal opens.
   */
  const loadData = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    setError(null);
    try {
      const [invoiceRes, catRes] = await Promise.all([
        api.getInvoice(invoiceId),
        api.getCategories()
      ]);
      setInvoice(invoiceRes.data);
      setCategories(catRes.data);
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (isOpen && invoiceId) {
      setInvoice(null);
      setUpdateMessage(null);
      loadData();
      // Focus the dialog element for accessibility
      setTimeout(() => dialogRef.current?.focus(), 0);
    }
    if (!isOpen) {
      setInvoice(null);
      setUpdateMessage(null);
      setError(null);
    }
  }, [isOpen, invoiceId, loadData]);

  /**
   * Build a display path from a category object.
   *
   * @param {Object} cat - Category with level1, level2, level3 fields
   * @returns {string} Formatted category path like "Food > Produce > Vegetables"
   */
  const getCategoryPath = (cat) => {
    return [cat.level1, cat.level2, cat.level3].filter(Boolean).join(' > ');
  };

  /**
   * Group active categories by their Level 1 value for optgroup rendering.
   * Returns an array of { label, categories } objects sorted by label.
   *
   * @returns {Array<{label: string, categories: Array}>} Grouped categories
   */
  const groupedCategories = useMemo(() => {
    const active = categories.filter(c => c.status === 'active');
    const groups = {};
    for (const cat of active) {
      const groupLabel = cat.level1 || 'Other';
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(cat);
    }
    return Object.keys(groups).sort().map(label => ({
      label,
      categories: groups[label]
    }));
  }, [categories]);

  /**
   * Handle category change for a line item.
   *
   * @param {number} lineItemId - The line item ID
   * @param {string} value - The selected category ID as a string, or empty for null
   */
  const handleCategoryChange = async (lineItemId, value) => {
    const categoryId = value === '' ? null : parseInt(value, 10);
    try {
      await api.updateLineItemCategory(lineItemId, { category_id: categoryId });
      setInvoice(prev => ({
        ...prev,
        line_items: prev.line_items.map(li => {
          if (li.id !== lineItemId) return li;
          const cat = categories.find(c => c.id === categoryId);
          return {
            ...li,
            category_id: categoryId,
            category_path: cat ? getCategoryPath(cat) : null,
            level1: cat ? cat.level1 : null,
            level2: cat ? cat.level2 : null,
            level3: cat ? cat.level3 : null
          };
        })
      }));
      setUpdateMessage({ type: 'success', text: 'Category updated' });
      setTimeout(() => setUpdateMessage(null), 3500);
    } catch (err) {
      console.error('Error updating category:', err);
      setUpdateMessage({ type: 'error', text: 'Failed to update category' });
    }
  };

  /**
   * Handle arrow key navigation for prev/next invoice.
   *
   * @param {KeyboardEvent} e - The keyboard event
   */
  const handleKeyDown = useCallback((e) => {
    if (onNavigate && invoices && invoices.length > 0) {
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      }
      if (e.key === 'ArrowRight' && currentIndex < invoices.length - 1) {
        onNavigate(currentIndex + 1);
      }
    }
  }, [onNavigate, invoices, currentIndex]);

  /**
   * Navigate to InvoiceEntry page with the current invoice loaded for editing.
   * Stores invoice ID in sessionStorage so InvoiceEntry can fetch fresh data.
   */
  const handleEditInvoice = useCallback(() => {
    if (!invoice) return;
    sessionStorage.setItem('editInvoiceData', JSON.stringify({ id: invoice.id }));
    onClose();
    navigate('../invoice-entry');
  }, [invoice, navigate, onClose]);

  const hasPrev = invoices && currentIndex > 0;
  const hasNext = invoices && currentIndex < (invoices || []).length - 1;

  /**
   * Build PDF header action buttons from invoice data.
   *
   * @returns {React.ReactNode|null} PDF buttons or unavailable indicator
   */
  const pdfHeaderActions = useMemo(() => {
    if (!invoice) return null;
    const pdfs = getPdfFilenames(invoice);
    if (pdfs.length === 0) {
      return (
        <span className="invoice-detail__pdf-unavailable">
          PDF not available
        </span>
      );
    }
    if (pdfs.length === 1) {
      return (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-label="View source PDF"
          onClick={() => openPdf(invoice.organization_id, pdfs[0])}
        >
          {'\uD83D\uDCC4'} View PDF
        </button>
      );
    }
    return pdfs.map((pdf, i) => (
      <button
        key={i}
        type="button"
        className="btn btn-ghost btn-sm"
        aria-label={`View PDF page ${i + 1}`}
        onClick={() => openPdf(invoice.organization_id, pdf)}
      >
        {'\uD83D\uDCC4'} Page {i + 1}
      </button>
    ));
  }, [invoice]);

  /**
   * Build subtitle breadcrumb text from invoice data.
   *
   * @returns {string|undefined} Breadcrumb text or undefined
   */
  const subtitle = invoice
    ? `Invoices > Invoice #${invoice.invoice_number || invoice.id}`
    : undefined;

  /**
   * Footer with prev/next/close navigation buttons.
   */
  const footerContent = (
    <>
      <div>
        {onNavigate && hasPrev && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onNavigate(currentIndex - 1)}
            type="button"
          >
            &larr; Previous
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp2)' }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleEditInvoice}
          type="button"
          disabled={!invoice}
        >
          Edit Invoice
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={onClose}
          type="button"
        >
          Close
        </button>
      </div>
      <div>
        {onNavigate && hasNext && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onNavigate(currentIndex + 1)}
            type="button"
          >
            Next &rarr;
          </button>
        )}
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invoice Details"
      subtitle={subtitle}
      size="full"
      headerActions={pdfHeaderActions}
      footer={footerContent}
      onKeyDown={handleKeyDown}
      className="invoice-detail__dialog"
    >
      <div ref={dialogRef} className="invoice-detail__body">
        {loading && <p>Loading invoice...</p>}
        {error && <p className="invoice-detail__error">{error}</p>}

        {invoice && (
          <>
            {/* Identification Metadata */}
            <div className="invoice-detail__info-grid">
              <div><strong>Invoice #:</strong> {invoice.invoice_number || '-'}</div>
              <div><strong>Date:</strong> {invoice.invoice_date}</div>
              <div><strong>Vendor:</strong> {invoice.vendor_name}</div>
              <div><strong>Business:</strong> {invoice.business_name}</div>
            </div>

            {/* Divider */}
            <hr className="invoice-detail__divider" />

            {/* Financial Summary */}
            <div className="invoice-detail__financial-grid">
              <div><strong>Subtotal:</strong> <span className="td-amt">${(invoice.subtotal || 0).toFixed(2)}</span></div>
              <div><strong>Tax:</strong> <span className="td-amt">${(invoice.tax || 0).toFixed(2)}</span></div>
              <div><strong>Fuel Surcharge:</strong> <span className="td-amt">${(invoice.fuel_surcharge || 0).toFixed(2)}</span>{invoice.fuel_surcharge_in_subtotal ? <span className="invoice-detail__fuel-note">(in subtotal)</span> : null}</div>
              <div><strong>Total:</strong> <span className="td-amt" style={{ fontWeight: 700 }}>${(invoice.total_amount || 0).toFixed(2)}</span></div>
            </div>

            {/* Invoice Notes */}
            {invoice.notes && (
              <div className="invoice-detail__notes">
                <strong>Notes:</strong> {invoice.notes}
              </div>
            )}

            {updateMessage && (
              <div className={`invoice-detail__update-message invoice-detail__update-message--${updateMessage.type}`}>
                {updateMessage.text}
              </div>
            )}

            {/* Line Items */}
            <h3 className="invoice-detail__section-heading">Line Items ({invoice.line_items.length})</h3>
            <p className="invoice-detail__help-text">
              Assign a spending category to each line item for analytics.
            </p>
            {invoice.line_items.length === 0 ? (
              <p className="invoice-detail__empty-text">No line items</p>
            ) : (
              <div className="tbl-wrap tbl-wrap--md">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">SKU</th>
                      <th scope="col">Item</th>
                      <th scope="col">Qty</th>
                      <th scope="col">Price</th>
                      <th scope="col">Total</th>
                      <th scope="col">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.line_items.map(li => {
                      const calculated = Math.round((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0) * 100) / 100;
                      const stored = parseFloat(li.total_price) || 0;
                      const hasDiscrepancy = Math.abs(stored - calculated) > 0.01;
                      const catPath = li.category_path || (li.level1 ? [li.level1, li.level2, li.level3].filter(Boolean).join(' > ') : null);
                      return (
                        <tr key={li.id}>
                          <td style={{ fontFamily: 'var(--font-m)' }}>{li.sku || '—'}</td>
                          <td>{li.item_name}</td>
                          <td style={{ fontFamily: 'var(--font-m)' }}>{li.quantity}</td>
                          <td><span className="td-amt">${(li.unit_price || 0).toFixed(2)}</span></td>
                          <td>
                            {hasDiscrepancy ? (
                              <span className="td-amt" style={{ color: 'var(--warning)' }} title={`Calculated: $${calculated.toFixed(2)} | Actual: $${stored.toFixed(2)}`}>
                                ${stored.toFixed(2)} !
                              </span>
                            ) : (
                              <span className="td-amt">${stored.toFixed(2)}</span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.82rem', color: 'var(--st500)' }}>{catPath || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Misc Charges */}
            {invoice.misc_charges && invoice.misc_charges.length > 0 && (
              <>
                <h3 className="invoice-detail__section-heading--misc">Misc Charges</h3>
                <div className="tbl-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Description</th>
                        <th scope="col">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.misc_charges.map((mc, i) => (
                        <tr key={i}>
                          <td>{mc.description}</td>
                          <td><span className="td-amt">${(mc.amount || 0).toFixed(2)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

InvoiceDetailModal.propTypes = {
  /** The invoice ID to display */
  invoiceId: PropTypes.number,
  /** Whether the modal is visible */
  isOpen: PropTypes.bool.isRequired,
  /** Callback when closing the modal */
  onClose: PropTypes.func.isRequired,
  /** Full list of invoices for prev/next navigation */
  invoices: PropTypes.array,
  /** Index of the current invoice in the list */
  currentIndex: PropTypes.number,
  /** Callback with new index for prev/next navigation */
  onNavigate: PropTypes.func,
};

export default InvoiceDetailModal;
